/**
 * IconWrapper — fixed-size flex container for an Icon instance.
 *
 * Figma source: `Icon-wrapper` component-set (componentId 548:11783).
 * Variants: size × 6 (12, 16, 20, 24, 32, 40) · counter × 1 (no) ·
 * original-color × 2 (no, yes). See design-system-manifest §5.4 for
 * full Figma geometry (layoutMode=HORIZONTAL, primaryAxisAlignItems=
 * SPACE_BETWEEN, counterAxisAlignItems=CENTER, no padding, no fills).
 *
 * Inspected variant: size=24 (nodeId 548:11802) — inline-flex container
 * hosting one `icon` child with SPACE_BETWEEN / CENTER alignment.
 *
 * Props:
 *  - size (12 | 16 | 20 | 24 | 32 | 40) — default 24
 *  - children (Icon instance)
 *  - className? passthrough
 *
 * Note on `original-color` / `counter` variants: the `counter=yes` set
 * catalog is empty in Figma (property declared, no variants). Not
 * implemented here until a real Figma variant provides geometry. If
 * you need an icon that keeps its own colour (e.g. the google logo in
 * `stage 07`), pass it as <Icon name="google" /> — <img> naturally
 * shows the file's own colors without currentColor inheritance.
 */

// Figma size (px) → Tailwind w/h class. Pulled from manifest §5.4.
const SIZE_CLASSES = {
  12: 'w-3 h-3',
  16: 'w-4 h-4',
  20: 'w-5 h-5',
  24: 'w-6 h-6',
  32: 'w-8 h-8',
  40: 'w-10 h-10',
};

export default function IconWrapper({ size = 24, className = '', children, ...rest }) {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES[24];
  const cls = [
    'inline-flex items-center justify-between',
    sizeClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={cls} {...rest}>
      {children}
    </span>
  );
}
